import { useEffect, type RefObject } from 'react';

/**
 * Ancla clic y hover al nodo de `externalTriggerRef` (sin botón interno en `Popup`).
 */
export function usePopupExternalTrigger(params: {
  externalTriggerRef: RefObject<HTMLElement | null> | undefined;
  disabled: boolean;
  hoverOpenEnabled: boolean;
  /** `true` cuando `openOnHover` y `hoverAnchorFollowPointer` están activos en `Popup`. */
  hoverAnchorFollowPointer: boolean;
  togglePopup: () => void;
  onHoverTriggerEnter: (event?: MouseEvent) => void;
  onHoverTriggerLeave: () => void;
  onHoverTriggerPointerMoveNative: (event: MouseEvent) => void;
}): void {
  const {
    externalTriggerRef,
    disabled,
    hoverOpenEnabled,
    hoverAnchorFollowPointer,
    togglePopup,
    onHoverTriggerEnter,
    onHoverTriggerLeave,
    onHoverTriggerPointerMoveNative,
  } = params;

  useEffect(() => {
    if (!externalTriggerRef) {
      return;
    }

    let cleaned = false;
    let cleanupListeners: (() => void) | undefined;

    const attach = () => {
      const el = externalTriggerRef.current;
      if (!el || cleaned) {
        return;
      }

      const onNativeClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        togglePopup();
      };

      el.addEventListener('click', onNativeClick);
      const removers: (() => void)[] = [() => el.removeEventListener('click', onNativeClick)];

      if (hoverOpenEnabled) {
        const onEnter = (e: MouseEvent) => {
          onHoverTriggerEnter(e);
        };
        el.addEventListener('mouseenter', onEnter);
        removers.push(() => el.removeEventListener('mouseenter', onEnter));

        el.addEventListener('mouseleave', onHoverTriggerLeave);
        removers.push(() => el.removeEventListener('mouseleave', onHoverTriggerLeave));

        if (hoverAnchorFollowPointer) {
          el.addEventListener('mousemove', onHoverTriggerPointerMoveNative, { passive: true });
          removers.push(() =>
            el.removeEventListener('mousemove', onHoverTriggerPointerMoveNative),
          );
        }
      }

      cleanupListeners = () => removers.forEach((r) => r());
    };

    const id = window.requestAnimationFrame(() => {
      attach();
    });

    return () => {
      cleaned = true;
      window.cancelAnimationFrame(id);
      cleanupListeners?.();
    };
  }, [
    externalTriggerRef,
    disabled,
    hoverOpenEnabled,
    hoverAnchorFollowPointer,
    togglePopup,
    onHoverTriggerEnter,
    onHoverTriggerLeave,
    onHoverTriggerPointerMoveNative,
  ]);
}
